import { combineLatest, from, Observable, timer } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import moment from 'moment';
import { groupBy } from 'lodash-es';
import { IndexedDatabaseInstance, IndexedDbService } from '@nimbus-library/features/shared/services/indexed-db.service';
import {
  IClientSideLogger,
  loggingDbName,
  loggingDbStorageName,
  loggingDbVersion,
  LogLevel,
  nimbusDbMigration,
} from '@nimbus-library/features/logger/services/client-logger/i-client-side-logger';

export interface ClientLogEntry {
  id: string;
  logName: string;
  message: string;
  date: string;
}
export class ClientSideLogger implements IClientSideLogger {
  private pendingEntries: ClientLogEntry[] = [];

  private readonly dbService: IndexedDbService = new IndexedDbService();

  private dbConnection$: Observable<IndexedDatabaseInstance>;
  public constructor() {
    this.dbConnection$ = from(this.dbService.openDatabase(loggingDbName, loggingDbStorageName, nimbusDbMigration, loggingDbVersion)).pipe(
      map((db) => db.db),
      shareReplay(1),
    );

    combineLatest([this.dbConnection$, timer(1000, 1000)])
      .pipe(
        switchMap(([dbConnection]) => {
          const pendingEntries = [...this.pendingEntries];
          this.pendingEntries = [];
          return from(this.writeEntries(dbConnection, pendingEntries));
        }),
      )
      .subscribe();
  }

  public writeMessage(logLevel: LogLevel, logName: string, message: string) {
    const date = moment();
    this.pendingEntries.push({
      id: `${logName}-${date.utc().format('YYYYMMDDHH')}`,
      date: date.toISOString(),
      message: `[${date.toISOString()}] ${logLevel}: ${message}`,
      logName,
    });
  }

  public proxyMessage(logName: string, message: string) {
    const date = moment();
    this.pendingEntries.push({
      id: `${logName}-${date.utc().format('YYYYMMDDHH')}`,
      date: date.toISOString(),
      message,
      logName,
    });
  }

  private async writeEntries(dbConnection: IndexedDatabaseInstance, entries: ClientLogEntry[]): Promise<void> {
    const entriesByIds = groupBy(entries, 'id');

    for (const id of Object.keys(entriesByIds)) {
      const existingEntries = (await dbConnection.get<ClientLogEntry[]>(id)) || [];
      const newEntries = entriesByIds[id];

      await dbConnection.put<ClientLogEntry[]>(id, [...existingEntries, ...newEntries]);
    }
  }
}
